import { getFormProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { LockClosedIcon } from '@heroicons/react/16/solid';
import { redirect, type ActionFunctionArgs } from '@remix-run/node';
import { useNavigation, useActionData, Form } from '@remix-run/react';
import { Button, Spinner } from 'flowbite-react';
import { useLocale } from 'remix-i18next/react';
import { typedjson } from 'remix-typedjson';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { FormInputFieldWithIcon } from '#common/components/catalyst-ui-extensions/FormInputFieldWithIcon.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { getUnifiedShopConfiguration } from '#common/models/unified-shop-settings.server.ts';
import { checkHoneypot } from '#common/utils/honeypot.server.ts';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { useVoicePath } from '#speaker-shop/hooks/useVoicePath';
import { getSpeakerShopData } from '#speaker-shop/models/speaker-shop-data.server.ts';
import { currentPasswordFingerprint, safeEqual, shopSessionStorage } from '#speaker-shop/session.server.ts';
import { resolveVoiceAndLangParams } from '#speaker-shop/utils/resolve-voice';
import { resolveShopContext } from '#speaker-shop/utils/shop-context.server.ts';

export const handle = { i18n: [ 'login' ] };

export const loginSchema = z.object({
    password: z.string(),
    redirectTo: z.string().optional()
});

export async function action({ request, params }: ActionFunctionArgs) {
    const { voiceSlug } = resolveVoiceAndLangParams(params);
    const shopContext = await resolveShopContext(request.url, voiceSlug);
    let expectedPassword: string | null;
    if (shopContext.unifiedShopPage && shopContext.speakerId) {
        expectedPassword = (await getUnifiedShopConfiguration(shopContext.speakerId)).shopPassword;
    } else {
        const speakerShopData = await getSpeakerShopData(request.url, voiceSlug);
        if (!speakerShopData) {
            throw new Response('No speaker shop data found', { status: 404 });
        }
        expectedPassword = speakerShopData.password;
    }

    if (expectedPassword) {
        const formData = await request.formData();
        await checkHoneypot(formData);

        const submission = parseWithZod(formData, { schema: loginSchema });
        if (submission.status !== 'success') {
            return typedjson(
                { ...submission.reply() },
                { status: submission.status === 'error' ? 400 : 200 }
            );
        }
        const submittedPassword = String(formData.get('password') ?? '');
        const cookie = request.headers.get('Cookie');
        const session = await shopSessionStorage.getSession(cookie);

        // Compare in constant time when lengths match; otherwise just fail.
        if (submittedPassword.length === expectedPassword.length && safeEqual(submittedPassword, expectedPassword)) {
            session.set('unlocked', true);
            session.set('fp', currentPasswordFingerprint(expectedPassword));

            throw redirect(submission.value.redirectTo ?? '/', {
                headers: {
                    'Set-Cookie': await shopSessionStorage.commitSession(session)
                }
            });
        }

        // Optional: throttle/lockout counters could be stored on the session or per IP.
        return typedjson(
            submission.reply({
                fieldErrors: {
                    password: [ 'Invalid password' ]
                }
            }),
            { status: 401 }
        );
    } else {
        throw redirect('/');
    }
}

export default function Login() {
    const { t, rawT } = useEditableTranslation('login');
    const locale = useLocale();
    const navigation = useNavigation();
    const isLoggingIn = navigation.state !== 'idle';
    const translatePath = useTranslatedPath(locale);
    const voicePath = useVoicePath();
    const actionData = useActionData<typeof action>();
    const [ form, fields ] = useForm({
        lastResult: actionData,
        constraint: getZodConstraint(loginSchema),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: loginSchema });
        }
    });

    return (
        <Form method='post' className='space-y-6' {...getFormProps(form)} noValidate>
            <HoneypotInputs />
            <FormInputFieldWithIcon
                field={fields.password}
                icon={<LockClosedIcon className='w-5 h-5 text-gray-500' />}
                placeholder={rawT('password')}
                type='password'
            />
            <input type='hidden' name='redirectTo' value={translatePath(voicePath('/'))} />
            <Button type='submit' disabled={isLoggingIn} className='w-full h-10 cursor-pointer' color='default'>
                {isLoggingIn ? <Spinner size='sm' /> : t('login')}
            </Button>
        </Form>
    );
}
